import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Coupon Selector Component
 *
 * Multi-select coupon component with search filtering and checkbox selection.
 * Used for excluding coupons from AI chatbot responses.
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, X, Ticket, Check } from 'lucide-react';
import { useTranslation } from '../../../hooks/useTranslation';
import { couponsApi } from '../../../utils/api';
const styles = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    searchContainer: {
        position: 'relative',
    },
    searchIcon: {
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#9ca3af',
        pointerEvents: 'none',
    },
    searchInput: {
        width: '100%',
        height: '40px',
        paddingLeft: '40px',
        paddingRight: '12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        color: '#111827',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        outline: 'none',
    },
    selectedChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        borderRadius: '6px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        fontSize: '13px',
        color: '#dc2626',
    },
    chipRemove: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        background: 'none',
        border: 'none',
        padding: '0',
        color: '#dc2626',
    },
    list: {
        maxHeight: '320px',
        overflowY: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        backgroundColor: '#ffffff',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '10px 14px',
        borderBottom: '1px solid #f3f4f6',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
    },
    rowHover: {
        backgroundColor: '#f9fafb',
    },
    rowSelected: {
        backgroundColor: '#fef2f2',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        border: '2px solid #d1d5db',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    checkboxChecked: {
        backgroundColor: '#dc2626',
        borderColor: '#dc2626',
    },
    couponInfo: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minWidth: 0,
    },
    couponCode: {
        fontWeight: 600,
        fontSize: '14px',
        color: '#111827',
        fontFamily: 'monospace',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 500,
    },
    badgePercent: {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
    },
    badgeFixed: {
        backgroundColor: '#dcfce7',
        color: '#15803d',
    },
    amount: {
        fontSize: '13px',
        color: '#6b7280',
    },
    expiry: {
        fontSize: '12px',
        color: '#9ca3af',
        marginLeft: 'auto',
        flexShrink: 0,
    },
    empty: {
        padding: '24px',
        textAlign: 'center',
        color: '#9ca3af',
        fontSize: '14px',
    },
};
function getDiscountTypeBadge(type) {
    switch (type) {
        case 'percent':
            return { label: '%', style: styles.badgePercent };
        case 'fixed_cart':
            return { label: 'Cart', style: styles.badgeFixed };
        case 'fixed_product':
            return { label: 'Product', style: styles.badgeFixed };
        default:
            return { label: type, style: styles.badgeFixed };
    }
}
function formatAmount(amount, type) {
    if (type === 'percent') {
        return `${amount}%`;
    }
    return `${amount}`;
}
export default function CouponSelector({ selectedIds, onChange, placeholder }) {
    const { t } = useTranslation();
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [hoveredId, setHoveredId] = useState(null);
    // Load all coupons on mount.
    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const data = await couponsApi.getAll();
                setCoupons(data);
            }
            catch (err) {
                console.error('Failed to load coupons:', err);
            }
            finally {
                setLoading(false);
            }
        };
        load();
    }, []);
    // Filter coupons by search term.
    const filteredCoupons = useMemo(() => {
        if (!search.trim())
            return coupons;
        const term = search.toLowerCase();
        return coupons.filter((c) => c.code.toLowerCase().includes(term) ||
            c.description.toLowerCase().includes(term));
    }, [coupons, search]);
    const toggleCoupon = useCallback((id) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter((sid) => sid !== id));
        }
        else {
            onChange([...selectedIds, id]);
        }
    }, [selectedIds, onChange]);
    const removeCoupon = useCallback((id) => {
        onChange(selectedIds.filter((sid) => sid !== id));
    }, [selectedIds, onChange]);
    // Get coupon by ID for chip display.
    const getCouponCode = (id) => {
        const coupon = coupons.find((c) => c.id === id);
        return coupon ? coupon.code : `#${id}`;
    };
    if (loading) {
        return (_jsx("div", { style: { padding: '24px', textAlign: 'center', color: '#6b7280' }, children: "Loading coupons..." }));
    }
    return (_jsxs("div", { style: styles.container, children: [selectedIds.length > 0 && (_jsx("div", { style: styles.selectedChips, children: selectedIds.map((id) => (_jsxs("span", { style: styles.chip, children: [_jsx(Ticket, { style: { width: '12px', height: '12px' } }), getCouponCode(id), _jsx("button", { style: styles.chipRemove, onClick: () => removeCoupon(id), title: "Remove", children: _jsx(X, { style: { width: '14px', height: '14px' } }) })] }, id))) })), _jsxs("div", { style: styles.searchContainer, children: [_jsx(Search, { style: { ...styles.searchIcon, width: '16px', height: '16px' } }), _jsx("input", { type: "text", style: styles.searchInput, placeholder: placeholder || t('searchCouponsToExclude'), value: search, onChange: (e) => setSearch(e.target.value) })] }), _jsx("div", { style: styles.list, children: filteredCoupons.length === 0 ? (_jsx("div", { style: styles.empty, children: t('noCouponsFound') })) : (filteredCoupons.map((coupon) => {
                    const isSelected = selectedIds.includes(coupon.id);
                    const isHovered = hoveredId === coupon.id;
                    const badge = getDiscountTypeBadge(coupon.discount_type);
                    return (_jsxs("div", { style: {
                            ...styles.row,
                            ...(isSelected ? styles.rowSelected : {}),
                            ...(isHovered ? styles.rowHover : {}),
                        }, onClick: () => toggleCoupon(coupon.id), onMouseEnter: () => setHoveredId(coupon.id), onMouseLeave: () => setHoveredId(null), children: [_jsx("div", { style: {
                                    ...styles.checkbox,
                                    ...(isSelected ? styles.checkboxChecked : {}),
                                }, children: isSelected && (_jsx(Check, { style: { width: '12px', height: '12px', color: '#ffffff' } })) }), _jsxs("div", { style: styles.couponInfo, children: [_jsx("span", { style: styles.couponCode, children: coupon.code }), _jsx("span", { style: { ...styles.badge, ...badge.style }, children: badge.label }), _jsx("span", { style: styles.amount, children: formatAmount(coupon.amount, coupon.discount_type) })] }), _jsx("span", { style: styles.expiry, children: coupon.expiry || t('noExpiry') })] }, coupon.id));
                })) })] }));
}
